import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, Clock } from "lucide-react";

const StepResult = ({ index, data, logs }) => {
    const hasError = logs?.some((l) => l.args?.some((a) => typeof a === "string" && a.toLowerCase().includes("error")));

    return (
        <div className={`border rounded-lg p-3 bg-white ${hasError ? "border-red-300" : "border-green-300"}`}>
            <div className="flex items-center gap-2 mb-2">
                {hasError
                    ? <XCircle className="h-4 w-4 text-red-500" />
                    : <CheckCircle className="h-4 w-4 text-green-500" />}
                <span className="font-medium text-sm">Step {index + 1}</span>
            </div>
            {data !== undefined && (
                <div className="mb-2">
                    <p className="text-xs font-medium text-slate-500 mb-1">Output</p>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-50 p-2 rounded max-h-64 overflow-auto">
                        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
                    </pre>
                </div>
            )}
            {logs && logs.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-slate-500 mb-1">Logs</p>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-900 text-slate-200 p-2 rounded max-h-48 overflow-auto">
                        {logs.map((l) => l.args?.join(" ")).join("\n")}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default function ExecutionTab({ execution, executionStatus }) {
    const { t } = useTranslation();

    if (executionStatus === "polling") {
        return (
            <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
                <Clock className="h-4 w-4 animate-spin" />
                {t("admin.automationPlayground.executionRunning")}
            </div>
        );
    }

    if (!execution) {
        return <p className="text-muted-foreground text-sm p-4">{t("admin.automationPlayground.noExecution")}</p>;
    }

    if (execution.error) {
        return (
            <div className="border border-red-300 rounded-lg p-4 bg-red-50 mt-3">
                <div className="flex items-center gap-2 mb-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="font-medium text-sm text-red-700">{t("admin.automationPlayground.executionFailed")}</span>
                </div>
                <pre className="text-xs font-mono whitespace-pre-wrap">{execution.error}</pre>
            </div>
        );
    }

    const contextData = execution.contextData || [];
    const logs = execution.logs || [];

    return (
        <div className="space-y-3 mt-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">{t("admin.automationPlayground.executionComplete")}</span>
            </div>
            {contextData.map((data, i) => (
                <StepResult key={i} index={i} data={data} logs={logs[i]} />
            ))}
            {contextData.length === 0 && (
                <p className="text-sm text-slate-400 italic p-4">{t("admin.automationPlayground.noStepResults")}</p>
            )}
        </div>
    );
}
